import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { EAGLEVIEW } from '@rsos/capstone/src/constants/capabilities';
import {
  JV_SELECTION,
  QV_SELECTION,
} from '@rsos/components/capstone/Layout/constants/views';
import { withLayout } from '@rsos/components/capstone/base/Layout';
import GoogleMap from '@rsos/components/capstone/common/Map/GoogleMap';
import useCenterMap from '@rsos/components/capstone/common/Map/useDeckGL/useCenterMap';
import { centerMapOnJursidiction } from '@rsos/components/capstone/common/Map/useDeckGL/utils';
import useDrawERA from '@rsos/components/capstone/common/Map/useDrawERA';
import { ALERTS, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { PREMIUM_EAGLEVIEW } from '@rsos/constants/permissions';
import useGoogleMapId from '@rsos/hooks/useGoogleMapId';
import useGoogleMapTransition from '@rsos/hooks/useGoogleMapTransition';
import useIsGoogleMapsFeatureFlagEnhanced from '@rsos/hooks/useIsGoogleMapsFeatureFlagEnhanced';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import {
  addSatelliteButtonClickListener,
  addZoomInButtonClickListener,
  addZoomOutButtonClickListener,
} from '@rsos/utils/googleMapClickListeners';
import { getGoogleMapsApiKey } from '@rsos/utils/metaTags';
import useAccess from '@rsos/utils/useAccess';
import { useIsMapIconDisabled } from '@rsos/utils/useIsMapIconDisabled';
import usePortalType from '@rsos/utils/usePortalType';
import MapCard from '../MapCard';
import Markers from './Markers';
import { RecenterMapButton } from './RecenterMapButton';
import DeckGLNext from './useDeckGL/DeckGLNext';

const apiKey = getGoogleMapsApiKey();

const MapWrapper = withLayout(styled.div`
  height: 100%;
  width: 100%;
  // Set height of parent div of GoogleMap so that GoogleMap can be dynamic
  // with height: 100%;
`);

const getIncident = (incidents, incidentType, incidentID) =>
  incidents[incidentType][incidentID];
const getIncidentLocations = (incidents, incidentType, incidentID) => {
  const incident = getIncident(incidents, incidentType, incidentID);
  if (incidentType === EMERGENCY_CALLS) {
    return incident.core.locations;
  }

  return incident.location_history.map(location => {
    return {
      ...location.geodetic,
      // created_at: location.created_at,
    };
  });
};

/**
 * Container for initializing Google Maps and handling drawing the polygon for
 * jurisdictions
 * @param {String} activeIncidentID - ID of alert/call being viewed
 * @param {String} activeQueueTab - which tab is being viewed in the queue.
 * ALERTS or EMERGENCY_CALLS
 * @param {Object} boundingBox - GeoJSON polygon to be drawn with Google Maps
 * @param {Object} currentERA - ERA data
 * @param {Boolean} hasW3w - Flag for checking if there is w3w data
 * @param {String} IDWithNewAlert - ID of newest alert
 * @param {String} inactiveIncidentID - call without location or removed alert
 * @param {Object} incidents - Incidents (alerts/calls). Note: name not final
 * @param {Object} incidents[ID] - Alert or caller ID of incident
 * @param {Object} incidents[ID].core - Core data for locations
 * @param {Array} incidents[ID].core.locations - List of locations to render on
 * the map for a given ID
 * @param {String} incidentType - ALERTS or EMERGENCY_CALLS
 * @param {Boolean} isAllView - Flag for if user is viewing all alerts/calls or
 * an individual alert/call
 * @param {boolean} isInternational - is international support enabled
 * @param {Object} mapView - JV_SELECTION or QV_SELECTION constant
 * @param {String} numberWithNewLocation - number with new location updates
 * @param {Function} onMapReady - Callback function when Map is ready for JV
 * @param {Function} onMarkerAdd - Callback function when a new marker is drawn
 * @param {Function} onMarkerClick - onClick handler for markers
 * @param {Function} onMarkerHover - Hover effect handler for markers
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * @param {String} removedID - ID of removed Alert or Call
 * @param {Function} setMafError - Function to set medicalert error
 * @param {Function} setADRError - Function to set adr error
 * @param {String} userProfile - ID of current user
 * @param {boolean} hasEagleViewAccess - access to Oblique Imagery
 */
const Map = ({
  activeIncidentID = null,
  activeQueueTab,
  boundingBox,
  currentERA,
  hasW3w,
  IDWithNewAlert = null,
  inactiveIncidentID = null,
  incidents = {},
  incidentType = EMERGENCY_CALLS,
  isAllView = true,
  isInternational = false,
  mapView = QV_SELECTION,
  numberWithNewLocation = null,
  onMapReady = () => {},
  onMarkerAdd = () => {},
  onMarkerClick = () => {},
  onMarkerHover = () => {},
  onRemoveID,
  removedID = null,
  setMafError,
  setADRError,
  userProfile = {},
  triggerViewJurisdiction,
  filteredCalls,
  hasCallStatus,
  handleObliqueImageryMarkerClicked,
}) => {
  const [mapRef, setMapRef] = useState(null);
  const [map, setMap] = useState(null);

  const googleMapId = useGoogleMapId();

  const [currentIncidents, setCurrentIncidents] = useState({
    [EMERGENCY_CALLS]: [],
    [ALERTS]: [],
  });
  const [currentIncidentsData, setCurrentIncidentsData] = useState([]);

  const { unreadAlerts, declinedAlertID } = useSelector(
    state => state.incidents,
  );

  const hoveredID = useSelector(state => state.hoveredIncident?.hoveredID);

  const w3wCoordinates = useSelector(
    state => state?.what3words?.searched.coordinates,
  );

  const { isPremiumUser } = usePortalType();
  const hasEagleViewAccess =
    !!useAccess(EAGLEVIEW, PREMIUM_EAGLEVIEW) && isPremiumUser;

  const isMapIconDisabled = useIsMapIconDisabled();

  const rspDeckGL = useIsGoogleMapsFeatureFlagEnhanced();

  useDrawERA(map, currentERA);

  // Set map instance in state to use with other components.
  // mapRef is used for dynamic calculation of height of map card
  const handleMapReady = (mapInstance, mapRef) => {
    onMapReady();
    setMapRef(mapRef);
    if (mapInstance) {
      mapInstance.addListener('click', evt => {
        const latLng = evt.latLng;
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line
          console.log('Coordinate:', [
            parseFloat(latLng.lng().toFixed(6)),
            parseFloat(latLng.lat().toFixed(6)),
          ]);
        }
      });

      if (isAllView) {
        centerMapOnJursidiction(boundingBox, mapInstance);
      }
    }
    setMap(mapInstance);
    setTimeout(() => {
      addSatelliteButtonClickListener();
      addZoomInButtonClickListener();
      addZoomOutButtonClickListener();
    }, 1000);
  };

  useCenterMap({
    map,
    boundingBox,
    hasW3w,
    triggerViewJurisdiction,
  });

  // Set incidents to add markers on map
  useEffect(() => {
    // TODO: remove. mapView is hardcoded to 'Jurisdiction'
    if (mapView === QV_SELECTION) {
      if (!activeIncidentID) {
        setCurrentIncidents({
          [EMERGENCY_CALLS]: [],
          [ALERTS]: [],
        });
      } else {
        // Note: No querying for alerts in QV. No need to check for it
        if (incidents && incidents.calls && incidents.calls[activeIncidentID]) {
          const mostRecentLocationsCalls = getIncidentLocations(
            incidents,
            incidentType,
            activeIncidentID,
          );
          if (
            !isEqual(
              currentIncidents[EMERGENCY_CALLS],
              mostRecentLocationsCalls,
            )
          )
            setCurrentIncidents({
              ...currentIncidents,
              [incidentType]: getIncidentLocations(
                incidents,
                incidentType,
                activeIncidentID,
              ).filter(location => location),
              // incidentType will be `EMERGENCY_CALLS`. can ignore alerts
            });
        }
      }
      setCurrentIncidentsData([]);
    } else {
      // If in detail view
      if (!isAllView) {
        if (Object.keys(incidents[incidentType]).includes(activeIncidentID)) {
          const incidentLocations = getIncidentLocations(
            incidents,
            incidentType,
            activeIncidentID,
          ).filter(incident => incident);

          const mostRecentData =
            incidentType === ALERTS
              ? [getIncident(incidents, ALERTS, activeIncidentID)]
              : [];

          if (
            incidentType === ALERTS &&
            !isEqual(currentIncidentsData, mostRecentData)
          ) {
            // Note: Any updates to alerts data will not get picked up by the
            // info window
            setCurrentIncidentsData(mostRecentData);
          } else if (
            incidentType === EMERGENCY_CALLS &&
            activeIncidentID &&
            currentIncidentsData.length
          ) {
            // Detailed view of call. Update currentIncidentsData to empty array
            setCurrentIncidentsData(mostRecentData);
          }
          // Generic handling for viewing historic locations for both alerts
          // and calls. Depending on how the data is stored, this supports
          // showing historic locations for alerts.
          // To prevent unnecessary map updates caused by rerenders by prop
          // updates, check if there are any changes between
          // currentIncidents/incidentLocations and
          // currentIncidentsData/mostRecentData if it is alerts view.
          // See MC-17761/MC-17100
          if (!isEqual(currentIncidents[incidentType], incidentLocations)) {
            setCurrentIncidents({
              [incidentType === ALERTS ? EMERGENCY_CALLS : ALERTS]: [],
              // Empty array for other incidentType for accurate markers
              [incidentType]: incidentLocations,
            });
          } else if (
            (isAllView || (incidentType === ALERTS && activeIncidentID)) &&
            currentIncidentsData.length === 1 &&
            currentIncidentsData[0].alert_id !== activeIncidentID
          ) {
            // When switching between alerts with the same location, the
            // condition above will fail. Compare previous alert_id with
            // activeIncidentID. Calls do not have this problem because a call's
            // `expiredTime` value will be updated when clicking on it
            setCurrentIncidents({
              [incidentType === ALERTS ? EMERGENCY_CALLS : ALERTS]: [],
              // Empty array for other incidentType for accurate markers
              [incidentType]: incidentLocations,
            });
          } else if (
            isEqual(currentIncidents[incidentType], incidentLocations) &&
            ((incidentType === ALERTS &&
              Object.keys(incidents[EMERGENCY_CALLS]).length &&
              Object.keys(currentIncidents[EMERGENCY_CALLS]).length) ||
              (incidentType === EMERGENCY_CALLS &&
                Object.keys(incidents[ALERTS]).length &&
                Object.keys(currentIncidents[ALERTS]).length))
          ) {
            // This condition handles updating the state for when there is
            // 1 call and 1 alert and one of them is clicked on. This updates
            // currentIncidents to trigger a redraw in Markers. See MC-20780
            setCurrentIncidents({
              [incidentType === ALERTS ? EMERGENCY_CALLS : ALERTS]: [],
              [incidentType]: incidentLocations,
            });
          }
        }
      } else {
        // All view. Parse most recent location of each type of incident
        const mostRecentLocationsCalls = Object.keys(incidents[EMERGENCY_CALLS])
          .map(incidentID => {
            const locations = getIncidentLocations(
              incidents,
              EMERGENCY_CALLS,
              incidentID,
            );
            return locations[locations?.length - 1];
          })
          .filter(location => location);
        const mostRecentLocationsAlerts = Object.keys(incidents[ALERTS]).map(
          incidentID => getIncidentLocations(incidents, ALERTS, incidentID)[0],
        );

        setCurrentIncidents({
          [EMERGENCY_CALLS]: mostRecentLocationsCalls,
          [ALERTS]: mostRecentLocationsAlerts,
        });

        // Assume this is for alerts. SAD will not render with Markers.
        // Data is used to map IDs to markers/circles/info windows
        const mostRecentData = Object.keys(incidents[ALERTS]).map(incident =>
          getIncident(incidents, ALERTS, incident),
        );
        if (!isEqual(mostRecentData, currentIncidentsData)) {
          setCurrentIncidentsData(mostRecentData);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents, isAllView]);

  // Google basemap transition handling
  useGoogleMapTransition(map);

  return (
    <MapWrapper activeIncidentID={activeIncidentID} incidentType={incidentType}>
      {map && (
        <>
          <DeckGLNext
            googleMapId={googleMapId}
            map={{ mapInstance: map }}
            removedID={removedID}
            activeQueueTab={activeQueueTab}
            isMapIconDisabled={isMapIconDisabled}
          />
          <MapCard
            hasEsriMapAccess={false}
            mapRef={mapRef}
            setMafError={setMafError}
            setADRError={setADRError}
          />
        </>
      )}

      <GoogleMap apiKey={apiKey} mapId={googleMapId} onReady={handleMapReady} />
      {!rspDeckGL && (
        <Markers
          activeIncidentID={activeIncidentID}
          activeQueueTab={activeQueueTab}
          boundingBox={boundingBox}
          currentIncidents={currentIncidents}
          currentIncidentsData={currentIncidentsData}
          declinedAlertID={declinedAlertID}
          filteredCalls={filteredCalls}
          handleObliqueImageryMarkerClicked={handleObliqueImageryMarkerClicked}
          hasCallStatus={hasCallStatus}
          hasEagleViewAccess={hasEagleViewAccess}
          hasW3w={hasW3w}
          hoveredID={hoveredID}
          IDWithNewAlert={IDWithNewAlert}
          inactiveIncidentID={inactiveIncidentID}
          incidentType={incidentType}
          isAllView={isAllView}
          isInternational={isInternational}
          isMapIconDisabled={isMapIconDisabled}
          map={map}
          mapView={mapView} // TODO: appears unused
          numberWithNewLocation={numberWithNewLocation}
          onMarkerAdd={onMarkerAdd}
          onMarkerClick={onMarkerClick}
          onMarkerHover={onMarkerHover}
          onRemoveID={onRemoveID}
          removedID={removedID}
          unreadAlerts={unreadAlerts}
          userProfile={userProfile}
          w3wCoordinates={w3wCoordinates}
        />
      )}
      {map ? (
        <RecenterMapButton mapProvider={GOOGLE} mapInstance={map} />
      ) : (
        <></>
      )}
    </MapWrapper>
  );
};

Map.propTypes = {
  activeIncidentID: PropTypes.string,
  expandedPolygon: PropTypes.object,
  IDWithNewAlert: PropTypes.string,
  incidents: PropTypes.object,
  incidentType: PropTypes.oneOf([EMERGENCY_CALLS, ALERTS]),
  isAllView: PropTypes.bool,
  isInternational: PropTypes.bool,
  mapView: PropTypes.oneOf([JV_SELECTION, QV_SELECTION]),
  numberWithNewLocation: PropTypes.string,
  onMapReady: PropTypes.func,
  onMarkerClick: PropTypes.func,
  onMarkerHover: PropTypes.func,
  removedID: PropTypes.string,
  userProfile: PropTypes.object,
  inactiveIncidentID: PropTypes.string,
};

export default Map;
